import { useMutation } from "@apollo/client";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { gql } from "generated-graphql";
import { ProcessingStrategy } from "generated-graphql/graphql";
import { useCallback } from "react";

import { useCurrentUser } from "./useCurrentUser";
import { useFeatureFlags } from "./useFeatureFlags";

const QUEUE_DOCUMENT_AI_ENRICHMENT_EXISTING = gql(`
  mutation QueueDocumentAIEnrichmentForManifest($input: QueueDocumentAIEnrichmentJobExistingInput!) {
    queueDocumentAIEnrichmentJobExisting(input: $input) {
      id
      status
    }
  }
`);

/**
 * Queue manifest document extraction via the unified Document AI enrichment pipeline.
 * The pipeline detects RCRA manifests and automatically runs manifest-specific extraction.
 *
 * Returns an array of { job, documentId } for status tracking in the upload dialog.
 */
export function useQueueManifestDocumentExtraction() {
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  const [mutate, { loading }] = useMutation(QUEUE_DOCUMENT_AI_ENRICHMENT_EXISTING);

  const canQueueManifestExtraction =
    isStaff &&
    !!featureFlags[FeatureFlagKey.ManifestDocumentIngest] &&
    !!featureFlags[FeatureFlagKey.WasteModule];

  const queueManifestDocumentExtraction = useCallback(
    async (input: {
      tenantId: string;
      documentIds: string[];
      facilityId?: string;
      manifestTrackingNumber?: string;
      generatorId?: string;
    }) => {
      const { data } = await mutate({
        variables: {
          input: {
            tenantId: input.tenantId,
            documentIds: input.documentIds,
            strategy: ProcessingStrategy.ExtractAllEmbedAll,
            manifestOverrides:
              input.facilityId || input.manifestTrackingNumber || input.generatorId
                ? input.documentIds.map((documentId) => ({
                    documentId,
                    facilityId: input.facilityId,
                    manifestTrackingNumber: input.manifestTrackingNumber,
                    generatorId: input.generatorId,
                  }))
                : undefined,
          },
        },
      });

      const job = data?.queueDocumentAIEnrichmentJobExisting;
      if (!job) return [];

      // Return one entry per document, all pointing to the same enrichment job
      return input.documentIds.map((documentId) => ({
        documentId,
        job: { id: job.id, status: job.status },
      }));
    },
    [mutate]
  );

  return { queueManifestDocumentExtraction, loading, canQueueManifestExtraction };
}
